import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Workflow, Search, Loader2 } from 'lucide-react';
import useAutomationsStore from '../../../store/useAutomationsStore';
import useAPI from '../../../hooks/useAPI';
import useCompany from '../../../hooks/useCompany';
import useModal from '../../../hooks/useModal';
import WorkflowCard from './components/WorkflowCard';
import CreateWorkflowModal, { CREATE_WORKFLOW_MODAL } from './components/CreateWorkflowModal';
import toast from 'react-hot-toast';

export default function Workflows() {
  const navigate = useNavigate();
  const api = useAPI();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id;
  const { workflows, isLoading, fetchWorkflows, createWorkflow, deleteWorkflow, toggleWorkflowStatus } = useAutomationsStore();
  const createModal = useModal(CREATE_WORKFLOW_MODAL);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (companyId) {
      fetchWorkflows(api, companyId);
    }
  }, [companyId]);

  const filtered = workflows.filter(
    (w) =>
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      (w.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (name, description) => {
    try {
      const workflow = await createWorkflow(api, companyId, name, description);
      navigate(`/dashboard/workflows/${workflow.id}`);
    } catch {
      toast.error('Failed to create workflow');
    }
  };

  const handleDelete = async (workflowId) => {
    try {
      await deleteWorkflow(api, companyId, workflowId);
      toast.success('Workflow deleted');
    } catch {
      toast.error('Failed to delete workflow');
    }
  };

  const handleToggle = async (workflowId) => {
    try {
      await toggleWorkflowStatus(api, companyId, workflowId);
    } catch {
      toast.error('Failed to update workflow');
    }
  };

  const handleOpen = (id) => {
    navigate(`/dashboard/workflows/${id}`);
  };

  return (
    <div className="2xl:p-8 p-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="2xl:text-2xl text-lg font-semibold text-gray-800">Workflows</h1>
          <p className="text-sm text-gray-400 mt-1">
            Automate your operations with visual diagram flows
          </p>
        </div>
        <button
          onClick={() => createModal.open({ onCreate: handleCreate })}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#2461DD] hover:bg-[#1d4fba] rounded-xl transition"
        >
          <Plus className="w-4 h-4" />
          New Workflow
        </button>
      </div>

      {workflows.length > 0 && (
        <div className="mb-6">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workflows..."
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((workflow) => (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              onOpen={handleOpen}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <Workflow className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-1">
            {search ? 'No workflows found' : 'No workflows yet'}
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            {search
              ? 'Try adjusting your search terms'
              : 'Create your first workflow to start automating operations'}
          </p>
          {!search && (
            <button
              onClick={() => createModal.open({ onCreate: handleCreate })}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#2461DD] hover:bg-[#1d4fba] rounded-xl transition"
            >
              <Plus className="w-4 h-4" />
              Create Workflow
            </button>
          )}
        </div>
      )}

      <CreateWorkflowModal />
    </div>
  );
}
